/**
 * Shared Login page component
 *
 * Renders a clean login form with email/password fields,
 * social login options, and forgot password / sign up links.
 * Designed to be reusable across different apps.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LoginCredentials } from '@startus/lib';
import { Toast } from './ui/Toast';
import { SocialLoginButtons } from './SocialLoginButtons';

interface LoginPageProps {
  login: (credentials: LoginCredentials) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  onLoginSuccess: () => void;
  userRole?: string;
  title?: string;
  // Social login handlers (optional)
  onGoogleLogin?: () => void;
  onMicrosoftLogin?: () => void;
  onAppleLogin?: () => void;
  onEventPlusLogin?: () => void;
  onMagicLink?: () => void;
  // Navigation handlers (optional)
  onForgotPassword?: () => void;
  onSignUp?: () => void;
  // When true, skip the full-page outer wrapper — caller is responsible for layout
  embedded?: boolean;
}

export default function LoginPage({
  login,
  isLoading,
  error,
  clearError,
  onLoginSuccess,
  userRole,
  title,
  onGoogleLogin,
  onMicrosoftLogin,
  onAppleLogin,
  onEventPlusLogin,
  onMagicLink,
  onForgotPassword,
  onSignUp,
  embedded = false,
}: LoginPageProps) {
  const { t } = useTranslation('common');

  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
  });

  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  // Local toast message — decoupled from volatile context error state.
  // Captured once when error becomes non-null so it survives re-renders
  // that might clear the context error (e.g. StrictMode cycles, routing).
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const errors: { email?: string; password?: string } = {};

    if (!credentials.email.trim()) {
      errors.email = t('validation.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credentials.email)) {
      errors.email = t('validation.email');
    }

    if (!credentials.password) {
      errors.password = t('validation.required');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Map userRole → source for the API payload
      const loginData: LoginCredentials = userRole
        ? { ...credentials, source: userRole }
        : credentials;
      await login(loginData);
      onLoginSuccess();
    } catch {
      // Error handled via error prop
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // Capture error into stable local state so Toast survives context re-renders.
  // error is the ApiError.code string (e.g. AUTH_INVALID_CREDENTIALS).
  // Look up errors.<code> in the common namespace; fall back to raw string if untranslated.
  useEffect(() => {
    if (error) {
      const key = `errors.${error}`;
      const translated = t(key, { ns: 'common' });
      setToastMessage(translated !== key ? translated : error);
    }
  }, [error, t]);

  // Clear context error on unmount to avoid stale state on next mount
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  // Inner form content — shared between standalone and embedded renders
  const content = (
    <>
      <h1 className="text-2xl font-bold text-center mb-6">{title || t('auth.welcomeBack')}</h1>

      {toastMessage && (
        <Toast
          message={toastMessage}
          type="error"
          onClose={() => {
            setToastMessage(null);
            clearError();
          }}
          position="top-center"
        />
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="form-control">
          <label className="label py-1">
            <span className="label-text">{t('auth.email')}</span>
          </label>
          <input
            type="email"
            name="email"
            className={`input w-full rounded-xl !border-0 !shadow-none ${validationErrors.email ? 'input-error' : ''}`}
            value={credentials.email}
            onChange={handleInputChange}
            disabled={isLoading}
          />
          {validationErrors.email && (
            <label className="label py-1">
              <span className="label-text-alt text-error">{validationErrors.email}</span>
            </label>
          )}
        </div>

        <div className="form-control">
          <label className="label py-1">
            <span className="label-text">{t('auth.password')}</span>
          </label>
          <input
            type="password"
            name="password"
            className={`input w-full rounded-xl !border-0 !shadow-none ${validationErrors.password ? 'input-error' : ''}`}
            value={credentials.password}
            onChange={handleInputChange}
            disabled={isLoading}
          />
          {validationErrors.password && (
            <label className="label py-1">
              <span className="label-text-alt text-error">{validationErrors.password}</span>
            </label>
          )}
        </div>

        {onForgotPassword && (
          <div className="text-right">
            <button
              type="button"
              onClick={onForgotPassword}
              className="link link-hover text-sm text-base-content/70"
            >
              {t('auth.forgotPassword')}
            </button>
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary w-full rounded-xl bg-[#19A2F8] border-[#19A2F8]"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="loading loading-spinner loading-sm"></span>
              {t('auth.signingIn')}
            </>
          ) : (
            t('auth.signIn')
          )}
        </button>
      </form>

      <SocialLoginButtons
        onGoogleLogin={onGoogleLogin}
        onMicrosoftLogin={onMicrosoftLogin}
        onAppleLogin={onAppleLogin}
        onEventPlusLogin={onEventPlusLogin}
        onMagicLink={onMagicLink}
        isLoading={isLoading}
      />

      {onSignUp && (
        <p className="text-center text-sm mt-6">
          {t('auth.noAccount')}{' '}
          <button type="button" onClick={onSignUp} className="link link-primary font-medium">
            {t('auth.signUpHere')}
          </button>
        </p>
      )}
    </>
  );

  // embedded=true: caller owns the layout (AuthLayout wraps content)
  if (embedded) return content;

  // Default: full-page standalone card layout
  return (
    <div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
      <div className="card w-full max-w-xs bg-base-100 shadow-xl">
        <div className="card-body p-8">{content}</div>
      </div>
    </div>
  );
}
